import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Thermometer, Zap, Wind, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const { t } = useTranslation();

  const categories = [
    {
      icon: Camera,
      title: t('categories.thermal'),
      image: 'https://images.pexels.com/photos/7859787/pexels-photo-7859787.jpeg?auto=compress&cs=tinysrgb&w=800',
      link: '/products?category=thermal',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Thermometer,
      title: t('categories.gas'),
      image: 'https://images.pexels.com/photos/6602917/pexels-photo-6602917.jpeg?auto=compress&cs=tinysrgb&w=800',
      link: '/products?category=gas',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Wind,
      title: t('categories.climate'),
      image: 'https://images.pexels.com/photos/9182633/pexels-photo-9182633.jpeg?auto=compress&cs=tinysrgb&w=800',
      link: '/products?category=climate',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Zap,
      title: t('categories.electrical'),
      image: 'https://images.unsplash.com/photo-1758295745926-88faff7ec2f2?w=800&q=80',
      link: '/products?category=electrical',
      color: 'from-yellow-500 to-orange-500'
    }
  ];

  const applications = [
    {
      title: t('applications.heating'),
      image: 'https://images.pexels.com/photos/6471911/pexels-photo-6471911.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: t('applications.food'),
      image: 'https://images.pexels.com/photos/9182633/pexels-photo-9182633.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: t('applications.pharma'),
      image: 'https://images.unsplash.com/photo-1748349221526-33b51820b21e?w=800&q=80'
    },
    {
      title: t('applications.industry'),
      image: 'https://images.unsplash.com/photo-1748002645678-7f9c04263315?w=800&q=80'
    }
  ];

  return (
    <div className="min-h-screen">


      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1748002757537-00ab5114135b?w=1920&q=80"
            alt="Hero Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center px-4 max-w-4xl"
        >
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t('hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white">
              <Link to="/products">
                {t('hero.cta')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white/20">
              <Link to="/contact">
                {t('hero.contact')}
              </Link>
            </Button>
          </div>
        </motion.div>
      </section>

      {/* Event Banner Section */}
      <section className="bg-gradient-to-r from-[#0060a9] to-[#0085ca] text-white relative overflow-hidden">
        {/* Abstract Fan Background Effect */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10 pointer-events-none">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full animate-spin-slow">
            <path fill="currentColor" d="M42.7,-62.9C50.9,-52.8,49.8,-34.4,56.7,-19.6C63.6,-4.8,78.5,6.4,79.5,18.9C80.5,31.4,67.6,45.2,53.2,53.2C38.8,61.2,22.9,63.4,8.8,61.7C-5.3,60,-17.6,54.4,-29.8,47.8C-42,41.2,-54.1,33.6,-61.6,22.6C-69.1,11.6,-72,-2.8,-68.6,-16.1C-65.2,-29.4,-55.5,-41.6,-43.8,-50.2C-32.1,-58.8,-18.4,-63.8,-2.7,-68C13,-72.2,28.7,-75.6,42.7,-62.9Z" transform="translate(100 100)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-12 md:py-16 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            {/* Left Content */}
            <div className="flex-1 space-y-6">
              <div className="flex items-center gap-4 mb-4">
                {/* AirVent Logo Placeholder */}
                <div className="flex items-center gap-2">
                  <img src="/images/logoColorLarge.png" alt="AIRVent" className="h-12 md:h-16 w-auto brightness-0 invert" />
                </div>
                <div className="h-8 w-px bg-white/30 mx-2"></div>
                <div className="text-sm md:text-base leading-tight opacity-90 block">
                  {t('event.coLocated')}
                  <br />
                  <span className="font-bold">aquatherm</span>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xl md:text-2xl font-semibold text-blue-100">{t('event.date')}</p>
                <p className="text-blue-200">{t('event.location')}</p>
              </div>

              <h2 className="text-3xl md:text-5xl font-bold leading-tight max-w-2xl">
                {t('event.title')}
              </h2>
            </div>

            {/* Right Action Boxes */}
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <div className="bg-white text-[#0060a9] p-6 rounded-xl shadow-lg flex-1 min-w-[200px] text-center transform hover:scale-105 transition-transform duration-300">
                <p className="text-sm font-bold uppercase tracking-wider mb-2">{t('event.visitStand')}</p>
                <p className="text-4xl font-black">{t('event.standNumber')}</p>
              </div>

              <div className="bg-white text-[#0060a9] p-6 rounded-xl shadow-lg flex-1 min-w-[200px] text-center transform hover:scale-105 transition-transform duration-300 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-4 h-4 bg-blue-400 rounded-bl-full group-hover:bg-blue-300 transition-colors"></div>
                <p className="text-lg font-bold uppercase tracking-wider mb-1">{t('event.register')}</p>
                <p className="text-xs font-medium text-gray-500">{t('event.promo')}</p>
                <ArrowRight className="w-6 h-6 mx-auto mt-3 text-blue-500 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('nav.products')}
            </h2>
            <p className="text-lg text-gray-600">{t('categories.explore')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  <Link to={category.link}>
                    <Card className="overflow-hidden h-full hover:shadow-xl transition-shadow duration-300">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={category.image}
                          alt={category.title}
                          className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                        />
                        <div className={`absolute inset-0 bg-gradient-to-t ${category.color} opacity-20`}></div>
                      </div>
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg bg-gradient-to-r ${category.color}`}>
                            <Icon className="h-6 w-6 text-white" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">{category.title}</h3>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section >

      {/* Why Choose Us */}
      < section className="py-20 px-4" >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('whyChoose.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('whyChoose.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: '🏆',
                title: t('whyChoose.leader.title'),
                description: t('whyChoose.leader.desc')
              },
              {
                icon: '✓',
                title: t('whyChoose.quality.title'),
                description: t('whyChoose.quality.desc')
              },
              {
                icon: '🔧',
                title: t('whyChoose.support.title'),
                description: t('whyChoose.support.desc')
              },
              {
                icon: '📦',
                title: t('whyChoose.solutions.title'),
                description: t('whyChoose.solutions.desc')
              },
              {
                icon: '🌍',
                title: t('whyChoose.presence.title'),
                description: t('whyChoose.presence.desc')
              },
              {
                icon: '💡',
                title: t('whyChoose.innovation.title'),
                description: t('whyChoose.innovation.desc')
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl mb-4">{feature.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section >

      {/* Applications Section */}
      < section className="py-20 px-4 bg-gray-50" >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('nav.applications')}
            </h2>
            <p className="text-lg text-gray-600">Solutions for various industries and applications</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {applications.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <Link to="/applications">
                  <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <div className="relative h-64">
                      <img
                        src={app.image}
                        alt={app.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="text-xl font-semibold text-white">{app.title}</h3>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section >



      {/* CTA Section */}
      < section className="py-20 px-4 bg-gray-900 text-white" >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t('cta.title')}
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                {t('cta.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white">
                  <Link to="/contact">
                    {t('hero.contact')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                  <Link to="/products">
                    {t('cta.browse')}
                  </Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-green-400 mb-2">24/7</div>
                  <p className="text-gray-300">{t('cta.support')}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-green-400 mb-2">ISO</div>
                  <p className="text-gray-300">{t('cta.iso')}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-green-400 mb-2">{t('cta.fast')}</div>
                  <p className="text-gray-300">{t('cta.fast')}</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-green-400 mb-2">{t('cta.free')}</div>
                  <p className="text-gray-300">{t('cta.free')}</p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section >
    </div >
  );
};

export default Home;